import React from 'react';
import Link from 'next/link';
import AssessmentFooter from '../AssessmentFooter';
import AssessmentSidePanel from '../AssessmentSidePanel';
import { QUESTIONS, PILLARS } from '../constants';
import { ArrowRight } from '../icons';
import { NewLogo } from '@/components/ui/icons/Logo';
import { useAssessmentContent } from '../content/context';

interface IntroScreenProps {
  onStart: () => void;
}

const IntroScreen: React.FC<IntroScreenProps> = ({ onStart }) => {
  const { navPrimary, navSecondary, intro } = useAssessmentContent();
  // Stat values are derived from the scoring logic; only the labels are CMS copy.
  const introStats = [
    { value: String(QUESTIONS.length), label: intro.statQuestionsLabel },
    { value: String(PILLARS.length), label: intro.statDimensionsLabel },
    { value: intro.statTimeValue, label: intro.statTimeLabel },
  ];

  return (
    <div className="animate-fade-in">
      <nav
        className="bg-white border-b border-a-border px-10 flex items-center justify-between sticky top-0 z-50"
        style={{ height: '58px' }}
      >
        <Link href="/">
          <NewLogo height="60" title="Foxbox Digital" />
        </Link>
        <div className="flex items-baseline" style={{ gap: '5px' }}>
          <span
            className="font-extrabold text-a-navy font-sans uppercase"
            style={{ fontSize: '15px', letterSpacing: '0.06em' }}
          >
            {navPrimary}
          </span>
          <span
            className="font-medium text-a-navy font-sans uppercase"
            style={{ fontSize: '15px', letterSpacing: '0.06em' }}
          >
            {navSecondary}
          </span>
        </div>
      </nav>

      <div className="bg-a-navy" style={{ minHeight: 'calc(100vh - 58px)' }}>
        <div className="grid sm:grid-cols-2 mx-auto" style={{ minHeight: 'calc(100vh - 58px)', maxWidth: '1280px' }}>
          <AssessmentSidePanel />

          <div className="bg-a-navy flex items-center justify-center" style={{ padding: '56px 48px' }}>
            <div
              className="w-full"
              style={{
                maxWidth: '400px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '10px',
                padding: '32px 32px 28px',
              }}
            >
              <div
                className="font-extrabold text-a-orange uppercase"
                style={{
                  fontSize: '11px',
                  letterSpacing: '0.10em',
                  marginBottom: '12px',
                }}
              >
                {intro.eyebrow}
              </div>
              <h2
                className="font-extrabold text-white font-sans uppercase mt-0"
                style={{
                  fontSize: '24px',
                  lineHeight: '1.15',
                  letterSpacing: '0.01em',
                  marginBottom: '14px',
                }}
              >
                {intro.title}
              </h2>
              <p
                className="font-light text-white m-0"
                style={{
                  fontSize: '12.5px',
                  lineHeight: '1.65',
                  color: 'rgba(255,255,255,0.78)',
                }}
              >
                {intro.subtitle}
              </p>

              <div
                className="grid grid-cols-3"
                style={{
                  margin: '24px 0',
                  padding: '18px 0',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                  gap: '8px',
                }}
              >
                {introStats.map((stat) => (
                  <div key={stat.label} className="flex flex-col">
                    <span className="font-extrabold text-white" style={{ fontSize: '20px', lineHeight: '1.1' }}>
                      {stat.value}
                    </span>
                    <span
                      className="font-bold uppercase"
                      style={{
                        fontSize: '9px',
                        letterSpacing: '0.08em',
                        color: 'rgba(255,255,255,0.5)',
                        marginTop: '4px',
                      }}
                    >
                      {stat.label}
                    </span>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={onStart}
                className="w-full bg-a-orange text-white border-0 rounded font-bold font-sans uppercase flex items-center justify-center gap-2 cursor-pointer hover:bg-a-orange-dark transition-colors"
                style={{
                  height: '46px',
                  fontSize: '13px',
                  letterSpacing: '0.05em',
                  outline: 'none',
                  boxShadow: 'none',
                }}
              >
                {intro.ctaLabel} <ArrowRight />
              </button>
            </div>
          </div>
        </div>
      </div>

      <AssessmentFooter />
    </div>
  );
};

export default IntroScreen;
